import { useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { tripsApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import LoadingState from "@/components/shared/LoadingState";
import ErrorState from "@/components/shared/ErrorState";
import RouteCard from "@/components/trips/RouteCard";

const editTripSchema = z.object({
  name: z.string().min(1, "Trip name is required").max(255),
  description: z.string().max(1000).optional().or(z.literal("")),
  active: z.boolean(),
});

type EditTripInput = z.infer<typeof editTripSchema>;

export default function EditTrip() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["trip", id],
    queryFn: () => tripsApi.getById(id!),
    enabled: !!id,
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<EditTripInput>({
    resolver: zodResolver(editTripSchema),
    defaultValues: { name: "", description: "", active: true },
  });

  useEffect(() => {
    if (data?.trip) {
      reset({
        name: data.trip.name,
        description: data.trip.description || "",
        active: data.trip.active,
      });
    }
  }, [data, reset]);

  const updateMutation = useMutation({
    mutationFn: (values: EditTripInput) => tripsApi.update(id!, values),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["trip", id] });
      queryClient.invalidateQueries({ queryKey: ["trips"] });
      navigate("/trips/" + id);
    },
  });

  if (isLoading) return <LoadingState message="Loading trip..." />;
  if (error) return <ErrorState message="Failed to load trip" onRetry={() => refetch()} />;
  if (!data?.trip) return <ErrorState message="Trip not found" />;

  const trip = data.trip;
  const routes = trip.routes || [];

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <h1 className="text-2xl font-bold tracking-tight">Edit Trip</h1>

      <form onSubmit={handleSubmit((v) => updateMutation.mutate(v))} className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Trip Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Trip Name</Label>
              <Input id="name" placeholder="My Trip" {...register("name")} />
              {errors.name && (
                <p className="text-destructive text-xs">{errors.name.message}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="desc">Description</Label>
              <textarea
                id="desc"
                className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                placeholder="Optional description"
                {...register("description")}
              />
              {errors.description && (
                <p className="text-destructive text-xs">{errors.description.message}</p>
              )}
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="active"
                className="rounded border-border"
                {...register("active")}
              />
              <Label htmlFor="active">Trip is active</Label>
            </div>
          </CardContent>
        </Card>

        {routes.length > 0 && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold">Routes (read-only)</h2>
            </div>
            <p className="text-sm text-muted-foreground">
              Routes cannot be modified after creation. Create a new trip if you need different routes.
            </p>
            {routes.map((route: import("@/lib/types").Route, i: number) => (
              <RouteCard key={route.id} route={route} index={i} />
            ))}
          </div>
        )}

        {updateMutation.isError && (
          <div className="rounded-md bg-destructive/10 border border-destructive/30 px-4 py-2 text-destructive text-sm">
            Failed to update trip. Please try again.
          </div>
        )}

        <div className="flex gap-3">
          <Button type="submit" disabled={updateMutation.isPending}>
            {updateMutation.isPending ? "Saving..." : "Save Changes"}
          </Button>
          <Button variant="outline" type="button" asChild>
            <Link to={"/trips/" + id}>Cancel</Link>
          </Button>
        </div>
      </form>
    </div>
  );
}
